/**
 * The distinct venues of a schedule, in first-appearance order, for the
 * Locations list that opens the Schedule section.
 *
 * The session rows say only the venue's short label — repeating
 * "1801 E Cotati Ave, Rohnert Park, CA 94928" seven times for seven services
 * says nothing the first time didn't — so this list is the one place the full
 * details live. Only venues with an address qualify: one that is just a name
 * has nothing more to introduce than its row already says, and the venue-TBD
 * placeholder never carries an address.
 *
 * Mirrors mercury's callVenues (mercurysrv/modernCall.go) on purpose: the
 * email and this page are meant to be the same document.
 */
export type CallVenue = {
  name: string;
  /** The abbreviation the session rows use once this list introduced it. */
  shortName: string;
  /** The full address, flattened to one comma-separated line. */
  address: string;
  /** Universal Google Maps search link — the app on a phone, the site elsewhere. */
  mapURL: string;
};

type VenueLike = {
  VenueName?: string;
  VenueShortName?: string;
  VenueAddress?: string;
};

/** GetFormatedAddress()'s street/locality lines on one line: the mapping
 * carries "1801 E Cotati Ave\nRohnert Park, CA 94928", and each line is tidied
 * of the trailing separators the formatter leaves when a part is absent. */
function flattenAddress(address: string): string {
  return address
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim().replace(/,$/, ""))
    .filter(Boolean)
    .join(", ");
}

/** The maps query is the name plus the address: the pair resolves to the
 * venue's place card where the address alone finds only the parcel. */
function venueMapURL(name: string, address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    [name, address].filter(Boolean).join(", "),
  )}`;
}

/**
 * @param {object[]} engagements the call's session groups
 * @returns {CallVenue[]} each addressed venue once, in first-appearance order
 */
export function callVenues(
  engagements?: readonly { Content?: readonly VenueLike[] }[],
): CallVenue[] {
  const seen = new Set<string>();
  const res: CallVenue[] = [];

  for (const group of engagements ?? []) {
    for (const session of group.Content ?? []) {
      const name = session.VenueName ?? "";
      const address = flattenAddress(session.VenueAddress ?? "");
      if (!name || !address) continue;

      const key = `${name}\n${address}`;
      if (seen.has(key)) continue;
      seen.add(key);

      res.push({
        name,
        shortName: session.VenueShortName ?? "",
        address,
        mapURL: venueMapURL(name, address),
      });
    }
  }

  return res;
}

/** What a session row calls its venue: the short label, or the full name for
 * venues (and mappings stored before short names) that don't have one. */
export function venueRowLabel(session: VenueLike): string {
  return session.VenueShortName || session.VenueName || "";
}

/** A session's venue as a maps link — the same link its Locations entry
 * carries, so the short label on the row navigates too. Empty (the row stays
 * plain text) for a venue without an address: there is no Locations entry to
 * agree with, and a name-only query rarely finds the place. */
export function venueRowMapURL(session: VenueLike): string {
  const name = session.VenueName ?? "";
  const address = flattenAddress(session.VenueAddress ?? "");
  if (!name || !address) return "";
  return venueMapURL(name, address);
}
